import { useEffect } from "react";
import { ToastProvider } from "./components/ui";
import { AdminAudit, AdminIntegrity } from "./pages/admin/Audit";
import { AdminDeveloperDetail } from "./pages/admin/DeveloperDetail";
import { AdminDevelopers } from "./pages/admin/Developers";
import { AdminGameDetail, AdminGames } from "./pages/admin/Games";
import { AdminOverview } from "./pages/admin/Overview";
import { DevAccount } from "./pages/dev/Account";
import { DevGame } from "./pages/dev/Game";
import { DevOverview } from "./pages/dev/Dashboard";
import { DevGames } from "./pages/dev/Games";
import { Landing } from "./pages/Landing";
import { Login } from "./pages/Login";
import { Pricing } from "./pages/Pricing";
import { CheckEmail, VerifyEmailPage } from "./pages/VerifyEmail";
import { NotFound } from "./pages/NotFound";
import { Redirect, RouterProvider, Routes, useLocation } from "./lib/router";
import { consoleUrl, docsHome, docsUrl, isConsolePath, isDocsHost, isDocsPath, isMarketingHost, marketingPath } from "./lib/site";
import { SessionProvider, useSession } from "./lib/session";

function MarketingConsoleRedirect() {
  const { pathname, search } = useLocation();
  const target = consoleUrl(pathname, search);
  useEffect(() => {
    window.location.replace(target);
  }, [target]);
  return null;
}

function DocsRedirect() {
  const { pathname, search } = useLocation();
  const target = docsUrl(pathname, search);
  useEffect(() => {
    window.location.replace(target);
  }, [target]);
  return null;
}

function ExternalRedirect({ to }: { to: string }) {
  useEffect(() => {
    window.location.replace(to);
  }, [to]);
  return null;
}

function Guarded() {
  const { session } = useSession();
  const { pathname } = useLocation();
  // Pricing lives on the marketing site (and on any host in local dev), signed in or not.
  if (pathname === "/pricing") {
    const target = marketingPath("/pricing");
    return target.startsWith("http") ? <ExternalRedirect to={target} /> : <Pricing />;
  }
  // If the console SPA ever loads on the docs host, send users to the static guide root.
  if (isDocsHost()) return <ExternalRedirect to={docsHome()} />;
  if (isDocsPath(pathname)) return <DocsRedirect />;
  if (isMarketingHost()) {
    if (pathname === "/") return <Landing />;
    if (isConsolePath(pathname)) return <MarketingConsoleRedirect />;
  }
  // The link from the verification email works signed in or not.
  if (pathname === "/verify-email") return <VerifyEmailPage />;
  if (!session) {
    if (pathname === "/") return isMarketingHost() ? <Landing /> : <Redirect to="/login" />;
    if (pathname === "/signup") return <Login mode="signup" />;
    return pathname === "/login" ? <Login /> : <><Login /><Redirect to="/login" /></>;
  }
  // Until the email is verified, the console is replaced by the check-your-email screen.
  if (session.role === "developer" && session.verificationPending) return <CheckEmail />;
  const home = session.role === "admin" ? "/admin" : "/dev";
  if (pathname === "/" || pathname === "/login" || pathname === "/signup") return <Redirect to={home} />;
  if (session.role === "admin" && pathname.startsWith("/dev")) return <Redirect to="/admin" />;
  if (session.role === "developer" && pathname.startsWith("/admin")) return <Redirect to="/dev" />;
  return (
    <Routes
      routes={[
        { path: "/admin", render: () => <AdminOverview /> },
        { path: "/admin/developers", render: () => <AdminDevelopers /> },
        { path: "/admin/developers/:id", render: (p) => <AdminDeveloperDetail key={p.id} id={p.id} /> },
        { path: "/admin/games", render: () => <AdminGames /> },
        { path: "/admin/games/:id", render: (p) => <AdminGameDetail key={p.id} id={p.id} /> },
        { path: "/admin/audit", render: () => <AdminAudit /> },
        { path: "/admin/integrity", render: () => <AdminIntegrity /> },
        { path: "/dev", render: () => <DevOverview /> },
        { path: "/dev/games", render: () => <DevGames /> },
        { path: "/dev/account", render: () => <DevAccount /> },
        { path: "/dev/credentials", render: () => <Redirect to="/dev/account#credentials" /> },
        { path: "/dev/games/:id", render: (p) => <DevGame key={p.id} id={p.id} tab="overview" /> },
        { path: "/dev/games/:id/:tab", render: (p) => <DevGame key={p.id} id={p.id} tab={p.tab} /> },
      ]}
      fallback={<NotFound home={home} />}
    />
  );
}

export function App() {
  return (
    <RouterProvider>
      <SessionProvider>
        <ToastProvider>
          <Guarded />
        </ToastProvider>
      </SessionProvider>
    </RouterProvider>
  );
}
